import {buttonVariants} from '@/components/app/button';
import {Icon} from '@/app/components/Icon';
import React, {useRef} from 'react';
import type {ReactNode} from 'react';
import {Link} from 'react-router-dom';
import {Notice} from '@/components/app/feedback';
import {cn} from '@/lib/utils';
import AuthBg from '../../../app/assets/img/auth-bg.webp';
import AuthBg2 from '../../../app/assets/img/auth-bg-2.jpeg';
import AuthBg3 from '../../../app/assets/img/auth-bg-3.jpeg';
import AuthBg4 from '../../../app/assets/img/auth-bg-4.jpeg';
import AuthBg5 from '../../../app/assets/img/auth-bg-5.jpeg';
import AuthBg6 from '../../../app/assets/img/auth-bg-6.jpeg';
import AuthBg7 from '../../../app/assets/img/auth-bg-7.jpeg';
import AuthBg8 from '../../../app/assets/img/auth-bg-8.jpeg';
import AuthBg9 from '../../../app/assets/img/auth-bg-9.jpeg';
import AuthBg10 from '../../../app/assets/img/auth-bg-10.jpeg';
import AuthBg11 from '../../../app/assets/img/auth-bg-11.jpeg';
import AuthBg12 from '../../../app/assets/img/auth-bg-12.jpeg';
import LogoDark from '../../../app/assets/img/logo-dark.webp';
import {useDevice} from '../../../app/hooks/useDevice';
import {getConfigValue} from '../../../app/utils/configUtils';
const images = [
  AuthBg,
  AuthBg2,
  AuthBg3,
  AuthBg4,
  AuthBg5,
  AuthBg6,
  AuthBg7,
  AuthBg8,
  AuthBg9,
  AuthBg10,
  AuthBg11,
  AuthBg12,
];
export interface AuthLayoutProps {
  backLabel?: string;
  backTo?: string;
  goDescription?: string;
  goLabel?: string;
  goTo?: string;
  imageSrc?: string;
  renderContent: () => ReactNode;
  subtitle: ReactNode;
  title: ReactNode;
}
export const AuthLayout = ({
  backLabel,
  backTo,
  goDescription,
  goLabel,
  goTo,
  imageSrc,
  renderContent,
  subtitle,
  title,
}: AuthLayoutProps) => {
  const {isMobile} = useDevice();
  const randomImageSrc = useRef(images[Math.floor(Math.random() * images.length)]).current;
  return (
    <div className="h-full flex flex-col">
      <div
        className="flex items-center text-white py-4 shrink-0 shadow-lg"
        style={{
          background: '#052244',
        }}>
        <div
          className="flex mx-auto"
          style={{
            width: '63rem',
          }}>
          <div className={'relative mx-auto xl:mx-0'}>
            <img alt="Biotandem" src={LogoDark} />
            <span className={'absolute flex text-xs left-0 bottom-0 text-white font-light ml-8 mb-1 pl-1'}>
              <span>v{getConfigValue('uiVersion')}</span>
              {import.meta.env.MODE !== 'production' && (
                <span className={'px-1 capitalize ml-auto'}>{import.meta.env.MODE}</span>
              )}
            </span>
          </div>
        </div>
        {goTo && (
          <div className="flex items-center gap-4 ml-auto">
            {goDescription}{' '}
            <Link
              className={buttonVariants({
                variant: 'outline',
                className:
                  'rounded-full border-white bg-transparent text-white no-underline hover:bg-white/10 hover:text-white hover:no-underline',
              })}
              to={goTo}>
              {goLabel}
            </Link>
          </div>
        )}
      </div>
      <Notice
        className={'py-2 z-1'}
        content={
          <div
            className={'text-center xl:text-left mx-auto'}
            style={{
              width: '63rem',
            }}>
            We constantly update our content. <br className="sm:hidden" />
            <Link
              className="text-brand"
              target={'_blank'}
              to={`${getConfigValue('landingUrl')}/news-and-updates/latest`}>
              Click to see news and updates.
            </Link>
          </div>
        }
        tone={'warn'}
      />
      <div
        className={cn('flex-1 min-h-0 overflow-auto', {
          'pt-16': isMobile,
        })}>
        <div className="flex grow shrink-0 min-h-full min-w-0 gap-12 p-4 md:p-12 items-center justify-center">
          <div className="relative w-[30rem] max-w-full shrink-0 z-1">
            <header>
              {backTo && (
                <Link className={buttonVariants({variant: 'link', className: 'absolute bottom-full px-1'})} to={backTo}>
                  <Icon icon="arrow-left" className="" />
                  <div className="">{backLabel}</div>
                </Link>
              )}
              <h1>{title}</h1>
              <p className="-mt-2 mb-6">{subtitle}</p>
            </header>
            {/* Auth forms keep the pill-shaped white inputs of the original design. */}
            <div className="**:data-[slot=input]:rounded-3xl **:data-[slot=input]:bg-card md:**:data-[slot=input]:text-base">
              {renderContent()}
            </div>
          </div>
          <div className="opacity-20 xl:opacity-100 absolute xl:relative xl:w-[30rem] max-w-full shrink-0">
            <div className="relative xl:w-fit rounded-[3rem] w-[calc(100vw-3rem)]">
              <img
                alt={`${title}`}
                className="w-full rounded-inherit max-w-[calc(50vw-3rem)] max-h-[calc(100vh-16rem)] max-xl:max-w-full max-xl:max-h-none xl:w-auto"
                src={imageSrc ?? randomImageSrc}
              />
              <div
                className="absolute inset-0 rounded-inherit -m-px"
                style={{
                  boxShadow: 'inset 0 0 3rem 1rem #F4F8F9, inset 0 0 2rem 1rem #f4f8f9, inset 0 0 1rem 1rem #f4f8f9',
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
